import { API_ENDPOINTS } from "@features/tickets/config/constants";
import type { TicketAttachment } from "@features/tickets/types/ticket.types";
import api from "@lib/api/client";
import { useEffect, useState } from "react";

/**
 * Fetches an attachment as a blob through the authenticated axios client and
 * exposes it as an object URL. Plain <img>/<iframe>/<a> requests cannot carry
 * the JWT, so the browser would get a 401 — the blob round-trip keeps the
 * Authorization header on every request. The object URL is revoked on cleanup.
 */
export function useAttachmentBlob(attachmentId: string | null) {
  const [objectUrl, setObjectUrl] = useState<string | null>(null);
  const [isPending, setIsPending] = useState(false);
  const [isError, setIsError] = useState(false);

  useEffect(() => {
    if (!attachmentId) {
      setObjectUrl(null);
      setIsPending(false);
      setIsError(false);
      return;
    }

    let cancelled = false;
    let createdUrl: string | null = null;

    setIsPending(true);
    setIsError(false);

    api
      .get(API_ENDPOINTS.TICKET_ATTACHMENTS.DOWNLOAD(attachmentId), { responseType: "blob" })
      .then((response) => {
        if (cancelled) return;
        createdUrl = URL.createObjectURL(response.data as Blob);
        setObjectUrl(createdUrl);
      })
      .catch(() => {
        if (!cancelled) setIsError(true);
      })
      .finally(() => {
        if (!cancelled) setIsPending(false);
      });

    return () => {
      cancelled = true;
      if (createdUrl) URL.revokeObjectURL(createdUrl);
    };
  }, [attachmentId]);

  return { objectUrl, isPending, isError };
}

/**
 * Downloads an attachment: fetches it as a blob (authenticated), then triggers
 * the browser download through a temporary anchor. The object URL is revoked
 * once the download has started.
 */
export async function downloadAttachment(attachment: TicketAttachment): Promise<void> {
  const response = await api.get(API_ENDPOINTS.TICKET_ATTACHMENTS.DOWNLOAD(attachment.id), {
    responseType: "blob",
  });
  const url = URL.createObjectURL(response.data as Blob);
  const anchor = document.createElement("a");
  anchor.href = url;
  anchor.download = attachment.originalFilename;
  document.body.appendChild(anchor);
  anchor.click();
  anchor.remove();
  window.setTimeout(() => URL.revokeObjectURL(url), 1000);
}
